import React from 'react';
import { Box, LinearProgress, Typography, Stack } from '@mui/material';
import { TransferOrder } from '../../types/TransferOrder.types';

interface ProgressBarProps {
  order: TransferOrder;
  phase?: 'allocation' | 'picking' | 'shipping' | 'receiving';
  showLabel?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ order, phase, showLabel = true }) => {
  const calculateProgress = (): { value: number; label: string } => {
    const totalRequested = order.TotalRequestedQuantity || order.TotalQuantity || 0;

    if (totalRequested === 0) {
      return { value: 0, label: '0%' };
    }

    switch (phase) {
      case 'allocation': {
        const allocated = order.TotalAllocatedQuantity || 0;
        const percentage = (allocated / totalRequested) * 100;
        return {
          value: percentage,
          label: `${allocated}/${totalRequested} allocated (${Math.round(percentage)}%)`,
        };
      }
      case 'picking': {
        const allocated = order.TotalAllocatedQuantity || 0;
        const picked = order.TotalPickedQuantity || 0;
        if (allocated === 0) {
          return { value: 0, label: '0%' };
        }
        const percentage = (picked / allocated) * 100;
        return {
          value: percentage,
          label: `${picked}/${allocated} picked (${Math.round(percentage)}%)`,
        };
      }
      case 'shipping': {
        const picked = order.TotalPickedQuantity || 0;
        const shipped = order.TotalShippedQuantity || 0;
        if (picked === 0) {
          return { value: 0, label: '0%' };
        }
        const percentage = (shipped / picked) * 100;
        return {
          value: percentage,
          label: `${shipped}/${picked} shipped (${Math.round(percentage)}%)`,
        };
      }
      case 'receiving': {
        const shipped = order.TotalShippedQuantity || 0;
        const received = order.TotalReceivedQuantity || 0;
        if (shipped === 0) {
          return { value: 0, label: '0%' };
        }
        const percentage = (received / shipped) * 100;
        return {
          value: percentage,
          label: `${received}/${shipped} received (${Math.round(percentage)}%)`,
        };
      }
      default: {
        // Overall progress based on status
        const received = order.TotalReceivedQuantity || 0;
        const percentage = (received / totalRequested) * 100;
        return {
          value: percentage,
          label: `${received}/${totalRequested} completed (${Math.round(percentage)}%)`,
        };
      }
    }
  };

  const { value, label } = calculateProgress();

  const getColor = (): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    if (value === 0) return 'error';
    if (value < 50) return 'warning';
    if (value < 100) return 'info';
    return 'success';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(value, 100)}
            color={getColor()}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>
        {showLabel && (
          <Box sx={{ minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {label}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ProgressBar;
